// 字体选择(齿轮菜单-字体):设备级设置,存 localStorage 不走后端。
// 候选为常见系统字体(日文/中文/通用),运行时用 document.fonts.check 探测本机
// 是否可用,只展示装了的字体;选中后把字体前置到 html 的 font-family
// (Twemoji Country Flags 必须保持栈首,国旗 emoji polyfill 依赖它)。
export interface FontOption {
  id: string;
  /** 展示名,用字体原生名称,不做翻译 */
  label: string;
  /** font-family 片段(可含多个别名) */
  css: string;
  /** document.fonts.check 探测用的单一字体名 */
  probe: string;
}

const FONT_CANDIDATES: FontOption[] = [
  // 日文黑体
  { id: 'hiragino-kaku', label: 'ヒラギノ角ゴシック', css: "'Hiragino Kaku Gothic ProN', 'Hiragino Sans'", probe: 'Hiragino Kaku Gothic ProN' },
  { id: 'yu-gothic', label: '游ゴシック', css: "'Yu Gothic UI', 'Yu Gothic'", probe: 'Yu Gothic' },
  { id: 'meiryo', label: 'メイリオ', css: 'Meiryo', probe: 'Meiryo' },
  { id: 'noto-sans-jp', label: 'Noto Sans JP', css: "'Noto Sans JP'", probe: 'Noto Sans JP' },
  // 日文明朝
  { id: 'yu-mincho', label: '游明朝', css: "'Yu Mincho', YuMincho", probe: 'Yu Mincho' },
  { id: 'hiragino-mincho', label: 'ヒラギノ明朝', css: "'Hiragino Mincho ProN'", probe: 'Hiragino Mincho ProN' },
  // 中文
  { id: 'pingfang', label: '苹方', css: "'PingFang SC'", probe: 'PingFang SC' },
  { id: 'yahei', label: '微软雅黑', css: "'Microsoft YaHei'", probe: 'Microsoft YaHei' },
  // 通用西文
  { id: 'arial', label: 'Arial', css: 'Arial', probe: 'Arial' },
];

// 与 global.less 中 html 的默认字体栈一致(去掉栈首的 Twemoji,由 apply 统一前置)
const DEFAULT_STACK = "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', "
  + "Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'";

const FONT_PREF_KEY = 'contentFontPreference';

/** 本机可用的候选字体(探测失败时全量返回,交给浏览器回退) */
export const getAvailableFonts = (): FontOption[] => {
  try {
    return FONT_CANDIDATES.filter((f) => document.fonts.check(`12px '${f.probe}'`));
  } catch (e) {
    return FONT_CANDIDATES;
  }
};

export const getFontPreference = (): string => {
  try {
    const saved = localStorage.getItem(FONT_PREF_KEY);
    return saved && FONT_CANDIDATES.some((f) => f.id === saved) ? saved : 'default';
  } catch (e) {
    return 'default';
  }
};

export const applyFontPreference = (id: string): void => {
  const font = FONT_CANDIDATES.find((f) => f.id === id);
  const root = document.documentElement;
  if (font) {
    // 用 CSS 变量 + data 标记,由 global.less 里的高优先级规则统一覆盖各容器
    // (.home-div/.menu/account 等都写死了 font-family,只设 html 会被它们盖掉,导致"选了没效果")
    root.style.setProperty('--user-font-family', `'Twemoji Country Flags', ${font.css}, ${DEFAULT_STACK}`);
    root.setAttribute('data-user-font', '');
  } else {
    // default:移除标记与变量,回到各自默认字体
    root.removeAttribute('data-user-font');
    root.style.removeProperty('--user-font-family');
  }
};

export const saveFontPreference = (id: string): void => {
  try {
    localStorage.setItem(FONT_PREF_KEY, id);
  } catch (e) {
    // localStorage 不可用时仅本次会话生效
  }
  applyFontPreference(id);
};
